import { Link } from 'react-router-dom';
import { 
  Award, Users, Clock, Shield, Star, ArrowRight, Phone,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';

const stats = [
  { value: '15+', label: 'Years Experience', icon: Clock },
  { value: '2,500+', label: 'Projects Completed', icon: Award },
  { value: '500+', label: 'Happy Customers', icon: Users },
  { value: '100%', label: 'Satisfaction Rate', icon: Star },
];

const values = [
  {
    icon: Award,
    title: 'Quality Craftsmanship',
    description: 'We take pride in every installation, ensuring meticulous attention to detail and superior workmanship.',
  },
  {
    icon: Users,
    title: 'Customer First',
    description: 'Your satisfaction is our priority. We listen to your needs and deliver solutions that exceed expectations.',
  },
  {
    icon: Shield,
    title: 'Integrity & Trust',
    description: 'Honest pricing, transparent communication, and reliable service you can count on.',
  },
  {
    icon: Clock,
    title: 'Timely Delivery',
    description: 'We respect your time and complete projects on schedule without compromising quality.',
  },
];

export const AboutPage = () => {
  return (
    <main className="pt-20" data-testid="about-page">
      {/* Page Header */}
      <section className="page-header py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
              About Us
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-amber-950 font-serif mb-6">
              Your Trusted Flooring Partner
            </h1>
            <p className="text-lg text-stone-600 leading-relaxed">
              Buy Fusion has been transforming homes and businesses with premium flooring solutions for over 15 years. Learn more about our story and commitment to excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 lg:py-28 section-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
                Our Story
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-amber-950 font-serif mb-6">
                Building Floors, Building Trust
              </h2>
              <p className="text-stone-600 text-lg leading-relaxed mb-6">
                Founded in Los Angeles, Buy Fusion started with a simple mission: to provide homeowners and businesses with exceptional flooring services at fair prices. What began as a small family operation has grown into one of the most trusted flooring companies in Southern California.
              </p>
              <p className="text-stone-600 text-lg leading-relaxed mb-6">
                Over the years, we've installed thousands of floors, from classic hardwood in historic homes to modern vinyl in contemporary offices. Our team of skilled craftsmen brings decades of combined experience to every project, ensuring results that stand the test of time.
              </p>
              <p className="text-stone-600 text-lg leading-relaxed">
                Today, we continue to uphold our founding values of quality, integrity, and customer satisfaction. When you choose Buy Fusion, you're choosing a partner committed to transforming your space with beautiful, durable flooring.
              </p>
            </div>
            <div className="img-zoom rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1704167674713-649193461719?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxoYXJkd29vZCUyMGZsb29yJTIwaW5zdGFsbGF0aW9uJTIwcHJvZmVzc2lvbmFsJTIwd29ya2VyfGVufDB8fHx8MTc3Mjc5NDEzMnww&ixlib=rb-4.1.0&q=85"
                alt="Professional flooring installation"
                className="w-full h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-amber-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <stat.icon className="w-10 h-10 text-amber-500 mx-auto mb-4" />
                <p className="text-4xl font-bold text-white font-serif mb-2">{stat.value}</p>
                <p className="text-amber-200">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 lg:py-28 section-stone noise-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
              Our Values
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-amber-950 font-serif mb-4">
              What We Stand For
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              These core values guide everything we do, from the materials we use to how we treat our customers.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-stone-100"
                data-testid={`value-${index}`}
              >
                <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-green-700" />
                </div>
                <h3 className="text-xl font-semibold text-amber-950 font-serif mb-3">{value.title}</h3>
                <p className="text-stone-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 lg:py-28 section-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="img-zoom rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1769736438943-3c57cbd02c22?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjB2aW55bCUyMGZsb29yaW5nJTIwbW9kZXJuJTIwaG9tZSUyMGludGVyaW9yfGVufDB8fHx8MTc3Mjc5NDEzM3ww&ixlib=rb-4.1.0&q=85"
                alt="Modern luxury interior with flooring"
                className="w-full h-96 object-cover"
              />
            </div>
            <div>
              <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
                Why Buy Fusion
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-amber-950 font-serif mb-6">
                The Buy Fusion Difference
              </h2>
              <ul className="space-y-4 mb-8">
                {[
                  'Licensed, bonded, and insured professionals',
                  'Free in-home consultations and estimates',
                  'Premium materials from trusted manufacturers',
                  'Workmanship warranty on all installations',
                  'Clean, respectful job sites',
                  'Flexible scheduling to fit your needs',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-stone-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact">
                <Button
                  className="bg-green-700 hover:bg-green-800 text-white rounded-full px-8 py-6 font-semibold"
                  data-testid="about-cta-btn"
                >
                  Get Free Estimate
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-amber-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white font-serif mb-6">
            Let's Create Beautiful Floors Together
          </h2>
          <p className="text-amber-100 text-lg mb-8 max-w-2xl mx-auto">
            Ready to transform your space? Contact our team today for a free consultation and see why thousands of homeowners trust Buy Fusion.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                className="bg-white text-amber-950 hover:bg-stone-100 rounded-full px-8 py-6 font-semibold"
                data-testid="about-page-cta-estimate"
              >
                Get Free Estimate
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+18888791244">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-amber-950 rounded-full px-8 py-6 font-semibold"
                data-testid="about-page-cta-call"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call +1-888-879-1244
              </Button>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
